import React, { useEffect, useState, lazy, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Star, ShieldCheck, Lock, XCircle, CheckCircle2, FileText, Zap, Gift, UserCheck, BadgeCheck, Shield } from 'lucide-react';
import { CountdownTimer } from './components/CountdownTimer';
import { CTAButton } from './components/CTAButton';
import { SocialProofPopup } from './components/SocialProofPopup';
import { AccordionItem } from './components/Accordion';
import { useLocalCurrency } from './hooks/useLocalCurrency';
const IMAGES = {
  product: 'https://cdn.magicpatterns.com/uploads/eARVb2eXJVJN1SACgJZv85/principal.png',
  bono1: 'https://cdn.magicpatterns.com/uploads/egSdXQngUdJRFCRVe1NVhf/Bono_1.png',
  bono2: 'https://cdn.magicpatterns.com/uploads/aGSSsK8zMWqxKYhKXmDSm5/bono_2.png',
  bono3: 'https://cdn.magicpatterns.com/uploads/6yWNYE9wDZgANN2NibC9a8/bono_3.png',
  bono4: 'https://cdn.magicpatterns.com/uploads/o3eZCAg9b9eh29AjDxR1qx/Bono_4.png',
  tomo1: 'https://cdn.magicpatterns.com/uploads/2CVh7wSrVnMdF7sNYB26ab/TOMO_1_.png',
  tomo2: 'https://cdn.magicpatterns.com/uploads/t9qWjN1pc41Mzd1CzqeTZY/TOMO_2.png',
  tomo3: 'https://cdn.magicpatterns.com/uploads/sAATZdEYhMsgcDmQzydfr7/TOMO_3.png',
  tomo4: 'https://cdn.magicpatterns.com/uploads/pPXomkts2RsypJaAvaWUWq/TOMO_4.png',
  tomo5: 'https://cdn.magicpatterns.com/uploads/vBRDaaYUaBHpMuqx2tQ7Py/TOMO_5.png'
};
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
export function App() {
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [urgencyTime, setUrgencyTime] = useState(() => {
    const saved = sessionStorage.getItem('mp_urgency_end');
    if (saved) {
      const remaining = Math.floor((parseInt(saved) - Date.now()) / 1000);
      return remaining > 0 ? remaining : 0;
    }
    const end = Date.now() + (10 * 60 + 53) * 1000;
    sessionStorage.setItem('mp_urgency_end', end.toString());
    return 10 * 60 + 53;
  });
  const {
    formatPrice,
    formatPriceCompact,
    currencyCode,
    countryFlag,
    isUSD,
    isLoading,
    checkoutUrl
  } = useLocalCurrency();
  // Defer Facebook Pixel loading
  useEffect(() => {
    const loadFBPixel = () => {
      // @ts-ignore
      !function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        b.head.appendChild(t);
      }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      // @ts-ignore
      fbq('init', '3070568583125644');
      // @ts-ignore
      fbq('init', '713766391617609');
      // @ts-ignore
      fbq('init', '1225428543097695');
      // @ts-ignore
      fbq('track', 'PageView');
    };
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(loadFBPixel);
    } else {
      setTimeout(loadFBPixel, 2000);
    }
  }, []);
  // Sticky CTA observer
  useEffect(() => {
    const purchaseEl = document.getElementById('purchase');
    if (!purchaseEl) return;
    const observer = new IntersectionObserver(([entry]) => setShowStickyCTA(entry.isIntersecting), {
      threshold: 0.1
    });
    observer.observe(purchaseEl);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const timer = setInterval(() => {
      setUrgencyTime(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const formatUrgencyTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor(seconds % 3600 / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };
  return <div className="font-sans text-white bg-slate-950 antialiased selection:bg-emerald-100 selection:text-emerald-900 pt-10 pb-20 md:pb-0 min-h-screen" data-id="element-0">
      {/* Preconnect + optimized font loading */}
      <link rel="preconnect" href="https://fonts.googleapis.com" data-id="element-1" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" data-id="element-2" />
      <link rel="preconnect" href="https://cdn.magicpatterns.com" data-id="element-3" />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Outfit:wght@500;700;800&display=swap" data-id="element-4" />
      <style data-id="element-5">{`
        h1, h2, h3, h4, h5, h6 { font-family: 'Outfit', sans-serif; }
        body { font-family: 'Inter', sans-serif; }
      `}</style>

      <CountdownTimer data-id="element-6" />
      <SocialProofPopup data-id="element-7" />

      {/* SECTION 1: HERO */}
      <section className="relative bg-slate-950 pt-12 pb-24 px-4 overflow-hidden border-b border-slate-800" data-id="element-8">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)',
        backgroundSize: '24px 24px'
      }} data-id="element-9" />

        <div className="max-w-6xl mx-auto relative z-10" data-id="element-10">
          <div className="flex flex-col items-center" data-id="element-11">
            <motion.div className="w-full max-w-4xl text-center" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} data-id="element-12">
              <div className="inline-block bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-bold px-4 py-1 rounded-full text-xs md:text-sm mb-6 uppercase tracking-wider" data-id="element-13">
                MEGAPACK LISTO PARA IMPRIMIR
              </div>

              <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold leading-tight mb-4 tracking-tight text-white" data-id="element-14">
                Todo tu año de Informática resuelto.
              </h1>

              <p className="text-2xl md:text-3xl font-bold text-emerald-400 mb-10" data-id="element-15">
                Listo para usar. Sin buscar más.
              </p>

              {/* Pack completo visual — tomos (above fold = eager) */}
              <div className="block relative group mb-12" data-id="element-16">
                <div className="flex justify-center" data-id="element-17">
                  <div className="relative mx-auto" style={{
                  width: '420px',
                  maxWidth: '100%',
                  height: '300px'
                }} data-id="element-18">
                    <div className="absolute inset-0 bg-emerald-500/10 blur-3xl rounded-full scale-90" data-id="element-19" />
                    <img src={IMAGES.tomo1} alt="Tomo 1" width="110" height="155" decoding="async" className="absolute rounded-xl shadow-2xl group-hover:scale-105 transition-transform duration-300 border border-slate-800" style={{
                    width: '110px',
                    left: '0px',
                    bottom: '10px',
                    transform: 'rotate(-12deg)',
                    zIndex: 10
                  }} data-id="element-20" />
                    <img src={IMAGES.tomo2} alt="Tomo 2" width="115" height="162" decoding="async" className="absolute rounded-xl shadow-2xl group-hover:scale-105 transition-transform duration-300 border border-slate-800" style={{
                    width: '115px',
                    left: '60px',
                    bottom: '20px',
                    transform: 'rotate(-6deg)',
                    zIndex: 20
                  }} data-id="element-21" />
                    <img src={IMAGES.tomo3} alt="Tomo 3" width="120" height="169" decoding="async" className="absolute rounded-xl shadow-2xl group-hover:scale-105 transition-transform duration-300 border border-slate-800" style={{
                    width: '120px',
                    left: '50%',
                    bottom: '28px',
                    transform: 'translateX(-50%) rotate(0deg)',
                    zIndex: 30
                  }} data-id="element-22" />
                    <img src={IMAGES.tomo4} alt="Tomo 4" width="115" height="162" decoding="async" className="absolute rounded-xl shadow-2xl group-hover:scale-105 transition-transform duration-300 border border-slate-800" style={{
                    width: '115px',
                    right: '60px',
                    bottom: '20px',
                    transform: 'rotate(6deg)',
                    zIndex: 20
                  }} data-id="element-23" />
                    <img src={IMAGES.tomo5} alt="Tomo 5" width="110" height="155" decoding="async" className="absolute rounded-xl shadow-2xl group-hover:scale-105 transition-transform duration-300 border border-slate-800" style={{
                    width: '110px',
                    right: '0px',
                    bottom: '10px',
                    transform: 'rotate(12deg)',
                    zIndex: 10
                  }} data-id="element-24" />
                  </div>
                </div>
              </div>

              <p className="text-lg md:text-xl text-slate-300 mb-8 leading-relaxed max-w-3xl mx-auto" data-id="element-25">
                El sistema que ya usan miles de docentes de informática para
                organizar todo el año en una sola tarde, sin improvisar clases,
                sin perder horas buscando material y sin tener que inventar
                actividades cada semana.
              </p>

              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 mb-10 max-w-3xl mx-auto shadow-2xl" data-id="element-26">
                <p className="text-xl font-medium text-white" data-id="element-27">
                  Más de{' '}
                  <span className="text-emerald-400 font-bold" data-id="element-28">
                    240 clases listas para usar
                  </span>
                  , organizadas por nivel y grado, con guiones claros,
                  actividades y evaluaciones.
                </p>
              </div>

              <div className="flex flex-wrap justify-center gap-3 mb-12" data-id="element-29">
                <span className="bg-slate-800 text-slate-300 px-4 py-2 rounded-full text-sm font-medium border border-slate-700 flex items-center gap-2" data-id="element-30">
                  <FileText className="w-4 h-4 text-emerald-400" data-id="element-31" /> 100% PDF
                </span>
                <span className="bg-slate-800 text-slate-300 px-4 py-2 rounded-full text-sm font-medium border border-slate-700 flex items-center gap-2" data-id="element-32">
                  <Zap className="w-4 h-4 text-emerald-400" data-id="element-33" /> Acceso inmediato
                </span>
                <span className="bg-slate-800 text-slate-300 px-4 py-2 rounded-full text-sm font-medium border border-slate-700 flex items-center gap-2" data-id="element-34">
                  <UserCheck className="w-4 h-4 text-emerald-400" data-id="element-35" /> Para
                  docentes de informática
                </span>
              </div>

              <div className="flex flex-col items-center gap-4" data-id="element-36">
                <CTAButton text="QUIERO MI MEGAPACK AHORA" size="xl" scrollToSection={true} data-id="element-37" />
                <div className="flex flex-wrap justify-center gap-4 text-xs text-slate-400 mt-2" data-id="element-38">
                  <span className="flex items-center gap-1" data-id="element-39">
                    <ShieldCheck className="w-3 h-3 text-emerald-400" data-id="element-40" />{' '}
                    Garantía 30 días
                  </span>
                  <span className="flex items-center gap-1" data-id="element-41">
                    <Lock className="w-3 h-3 text-emerald-400" data-id="element-42" /> Pago 100%
                    seguro
                  </span>
                  <span className="flex items-center gap-1" data-id="element-43">
                    <Zap className="w-3 h-3 text-emerald-400" data-id="element-44" /> Acceso
                    inmediato
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 2: SOCIAL PROOF BAR */}
      <section className="py-12 px-4 bg-slate-900 border-b border-slate-800" data-id="element-45">
        <div className="max-w-6xl mx-auto" data-id="element-46">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center mb-12" data-id="element-47">
            <div data-id="element-48">
              <div className="text-4xl font-extrabold text-emerald-400 mb-2" data-id="element-49">
                +3.000
              </div>
              <div className="text-slate-300 font-medium" data-id="element-50">docentes confían</div>
            </div>
            <div data-id="element-51">
              <div className="text-4xl font-extrabold text-emerald-400 mb-2" data-id="element-52">
                5
              </div>
              <div className="text-slate-300 font-medium" data-id="element-53">
                tomos completamente listos
              </div>
            </div>
            <div data-id="element-54">
              <div className="text-4xl font-extrabold text-emerald-400 mb-2" data-id="element-55">
                4.9/5
              </div>
              <div className="text-slate-300 font-medium" data-id="element-56">
                calificación promedio
              </div>
            </div>
          </div>
          <p className="text-center text-slate-400 uppercase tracking-widest text-sm font-bold mb-8" data-id="element-57">
            Para docentes de informática
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-58">
            {[{
            text: 'En una tarde pude organizar todo el año.',
            name: 'Patricia Sánchez',
            age: 38,
            location: 'Santiago, Chile'
          }, {
            text: 'Por fin tengo clases estructuradas y claras.',
            name: 'Daniel Pérez',
            age: 43,
            location: 'La Paz, Bolivia'
          }, {
            text: 'Me ahorró horas de preparación cada semana.',
            name: 'Daniela Paredes',
            age: 31,
            location: 'Medellín, Colombia'
          }].map((t, i) => <div key={i} className="bg-slate-800 border border-slate-700 p-6 rounded-2xl shadow-lg" data-id="element-59">
                <div className="flex gap-1 text-yellow-400 mb-3" data-id="element-60">
                  {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-current" data-id="element-61" />)}
                </div>
                <p className="text-white italic mb-4" data-id="element-62">"{t.text}"</p>
                <div className="text-sm" data-id="element-63">
                  <p className="text-white font-bold" data-id="element-64">
                    {t.name}, {t.age} años
                  </p>
                  <p className="text-slate-400" data-id="element-65">{t.location}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 3: PROBLEM */}
      <section className="py-16 px-4 bg-slate-950 border-b border-slate-800" data-id="element-66">
        <div className="max-w-4xl mx-auto" data-id="element-67">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-68">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-69">
              Si sos docente de informática, esto seguro te suena familiar:
            </h2>
          </motion.div>
          <div className="space-y-4" data-id="element-70">
            {['Domingo a la noche buscando material que nunca queda bien', 'Clases improvisadas porque no tenés una planificación clara', 'Actividades que no logran enganchar a los alumnos', 'Evaluaciones hechas a último momento', 'Tener que inventar contenido nuevo para cada clase'].map((item, i) => <motion.div key={i} className="bg-slate-900 border border-slate-800 p-5 rounded-2xl flex items-center gap-4 shadow-md" initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: i * 0.1
          }} viewport={{
            once: true
          }} data-id="element-71">
                <div className="bg-red-500/10 p-2 rounded-full shrink-0" data-id="element-72">
                  <XCircle className="w-6 h-6 text-red-500" data-id="element-73" />
                </div>
                <span className="text-slate-300 text-lg" data-id="element-74">{item}</span>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* SECTION 4: EMOTIONAL TRANSITION */}
      <section className="py-24 px-4 bg-slate-900 border-b border-slate-800 text-center" data-id="element-75">
        <div className="max-w-4xl mx-auto" data-id="element-76">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-10" data-id="element-77">
            ¿Cuántos domingos más vas a perder preparando clases{' '}
            <span className="text-orange-400 underline decoration-orange-500/50 underline-offset-8" data-id="element-78">
              desde cero
            </span>
            ?
          </h2>
          <CTAButton text="QUIERO MI MEGAPACK AHORA" size="lg" scrollToSection={true} data-id="element-79" />
        </div>
      </section>

      {/* SECTION 5: PRODUCT PRESENTATION */}
      <section className="py-20 px-4 bg-slate-950 border-b border-slate-800 text-center" data-id="element-80">
        <div className="max-w-5xl mx-auto" data-id="element-81">
          <div className="inline-block bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-bold px-4 py-1 rounded-full text-xs md:text-sm mb-6 uppercase tracking-wider" data-id="element-82">
            MEGAPACK LISTO PARA IMPRIMIR
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4" data-id="element-83">
            MEGAPACK INFORMÁTICA LISTA
          </h2>
          <p className="text-2xl font-bold text-emerald-400 mb-6" data-id="element-84">
            5 tomos organizados por nivel y grado.
          </p>
          <p className="text-xl text-slate-300 mb-4 max-w-3xl mx-auto" data-id="element-85">
            Más de 240 clases listas para usar, con guión completo, actividades
            paso a paso y evaluaciones listas.
          </p>
          <p className="text-lg text-slate-400 mb-12 max-w-2xl mx-auto" data-id="element-86">
            Todo preparado para que puedas abrir el material y empezar a usarlo
            inmediatamente.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-16" data-id="element-87">
            {['Con guión de clase', 'Con evaluaciones', 'Con actividades listas', 'Con planificación incluida'].map((item, i) => <div key={i} className="bg-slate-800 border border-emerald-500/30 px-6 py-3 rounded-full flex items-center gap-2 shadow-lg" data-id="element-88">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" data-id="element-89" />
                <span className="font-medium text-white" data-id="element-90">{item}</span>
              </div>)}
          </div>
          <div className="block relative group" data-id="element-91">
            <div className="flex justify-center" data-id="element-92">
              <div className="relative mx-auto" style={{
              width: '420px',
              maxWidth: '100%',
              height: '300px'
            }} data-id="element-93">
                <div className="absolute inset-0 bg-emerald-500/10 blur-3xl rounded-full scale-90" data-id="element-94" />
                <img src={IMAGES.tomo1} alt="Tomo 1" loading="lazy" decoding="async" className="absolute rounded-xl shadow-2xl group-hover:scale-105 transition-transform duration-300 border border-slate-800" style={{
                width: '110px',
                left: '0px',
                bottom: '10px',
                transform: 'rotate(-12deg)',
                zIndex: 10
              }} data-id="element-95" />
                <img src={IMAGES.tomo2} alt="Tomo 2" loading="lazy" decoding="async" className="absolute rounded-xl shadow-2xl group-hover:scale-105 transition-transform duration-300 border border-slate-800" style={{
                width: '115px',
                left: '60px',
                bottom: '20px',
                transform: 'rotate(-6deg)',
                zIndex: 20
              }} data-id="element-96" />
                <img src={IMAGES.tomo3} alt="Tomo 3" loading="lazy" decoding="async" className="absolute rounded-xl shadow-2xl group-hover:scale-105 transition-transform duration-300 border border-slate-800" style={{
                width: '120px',
                left: '50%',
                bottom: '28px',
                transform: 'translateX(-50%) rotate(0deg)',
                zIndex: 30
              }} data-id="element-97" />
                <img src={IMAGES.tomo4} alt="Tomo 4" loading="lazy" decoding="async" className="absolute rounded-xl shadow-2xl group-hover:scale-105 transition-transform duration-300 border border-slate-800" style={{
                width: '115px',
                right: '60px',
                bottom: '20px',
                transform: 'rotate(6deg)',
                zIndex: 20
              }} data-id="element-98" />
                <img src={IMAGES.tomo5} alt="Tomo 5" loading="lazy" decoding="async" className="absolute rounded-xl shadow-2xl group-hover:scale-105 transition-transform duration-300 border border-slate-800" style={{
                width: '110px',
                right: '0px',
                bottom: '10px',
                transform: 'rotate(12deg)',
                zIndex: 10
              }} data-id="element-99" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: WHAT'S INCLUDED */}
      <section className="py-20 px-4 bg-slate-900 border-b border-slate-800" data-id="element-100">
        <div className="max-w-5xl mx-auto" data-id="element-101">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-16" data-id="element-102">
            Esto es exactamente lo que recibís:
          </h2>
          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-8 max-w-3xl mx-auto" data-id="element-103">
            <h3 className="text-xl font-bold text-white mb-6 text-center" data-id="element-104">
              Cada tomo incluye:
            </h3>
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-105">
              {['Planificación anual lista para usar', 'Guión completo de clase paso a paso', 'Actividades prácticas para alumnos', 'Rúbricas y criterios de evaluación', 'Material imprimible', 'Proyectos tecnológicos listos para trabajar en clase'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-106">
                  <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" data-id="element-107" />
                  <span className="text-slate-300" data-id="element-108">{item}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7: BONUSES */}
      <section className="py-20 px-4 bg-slate-950 border-b border-slate-800" data-id="element-109">
        <div className="max-w-6xl mx-auto" data-id="element-110">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-16 max-w-3xl mx-auto" data-id="element-111">
            Además recibís{' '}
            <span className="text-emerald-400" data-id="element-112">4 bonos estratégicos</span>{' '}
            incluidos sin costo extra
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6" data-id="element-113">
            {[{
            title: 'Planificación Express 12 Meses',
            desc: 'Organiza todo tu año académico en minutos con una estructura simple que podés adaptar a cualquier curso.',
            img: IMAGES.bono1
          }, {
            title: '30 Clases Salvavidas',
            desc: 'Clases listas para usar cuando no tuviste tiempo de preparar material o necesitás una solución rápida.',
            img: IMAGES.bono2
          }, {
            title: 'Banco de Evaluaciones',
            desc: 'Evaluaciones listas para usar con criterios claros y actividades rápidas de aplicar.',
            img: IMAGES.bono3
          }, {
            title: 'Manual de Manejo de Aula Digital',
            desc: 'Estrategias simples para manejar clases con tecnología sin caos ni desorden.',
            img: IMAGES.bono4
          }].map((bono, i) => <div key={i} className="bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-lg flex flex-col items-center text-center hover:border-emerald-500/50 transition-colors" data-id="element-114">
                <div className="w-12 h-12 bg-emerald-500/10 rounded-full flex items-center justify-center mb-4" data-id="element-115">
                  <Gift className="w-6 h-6 text-emerald-400" data-id="element-116" />
                </div>
                {bono.img && <img src={bono.img} alt={bono.title} loading="lazy" decoding="async" className="w-32 mb-4 rounded-lg shadow-md" data-id="element-117" />}
                <h3 className="text-xl font-bold text-white mb-3" data-id="element-118">
                  {bono.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed" data-id="element-119">
                  {bono.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 8: URGENCY */}
      <section className="py-20 px-4 bg-slate-900 border-b border-slate-800 text-center" data-id="element-120">
        <div className="max-w-3xl mx-auto" data-id="element-121">
          <h2 className="text-3xl font-bold text-white mb-8" data-id="element-122">
            El precio de lanzamiento termina en:
          </h2>
          <div className="text-6xl md:text-8xl font-mono font-extrabold text-orange-400 mb-8 tracking-tighter drop-shadow-[0_0_15px_rgba(251,146,60,0.3)]" data-id="element-123">
            {formatUrgencyTime(urgencyTime)}
          </div>
          <p className="text-xl text-slate-300 mb-2" data-id="element-124">
            Después el precio sube y no vuelve a bajar.
          </p>
          <p className="text-lg text-red-400 font-medium mb-10" data-id="element-125">
            Quedan pocos accesos disponibles al precio actual.
          </p>
          <CTAButton text="ASEGURAR MI PRECIO AHORA" size="xl" scrollToSection={true} data-id="element-126" />
        </div>
      </section>

      {/* SECTION 9: PRICING */}
      <section id="purchase" className="py-20 px-4 bg-slate-900 text-white" data-id="element-127">
        <div className="max-w-4xl mx-auto text-center" data-id="element-128">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-129">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-130">
              🎓 Todo lo que incluye tu MegaPack
            </h2>
            <div className="flex flex-wrap justify-center gap-4 text-slate-400 mb-8 text-sm md:text-base items-center" data-id="element-131">
              <span data-id="element-132">
                5 Tomos:{' '}
                <span className="line-through" data-id="element-133">{formatPriceCompact(33)}</span>
              </span>
              <span data-id="element-134">+</span>
              <span data-id="element-135">
                Bonos:{' '}
                <span className="line-through" data-id="element-136">{formatPriceCompact(14)}</span>
              </span>
              <span data-id="element-137">=</span>
              <span className="text-white font-bold" data-id="element-138">
                Valor total: {formatPrice(47)}
              </span>
            </div>
            <div className="inline-block bg-emerald-600 text-white px-6 py-2 rounded-full font-bold text-lg mb-6 animate-pulse shadow-[0_0_20px_rgba(16,185,129,0.5)]" data-id="element-139">
              🔥 Hoy acceso completo por solo {formatPrice(13.99)}
            </div>

            {/* Trust strip */}
            <div className="flex flex-wrap justify-center gap-6 mb-10 text-sm text-slate-300" data-id="element-140">
              <span className="flex items-center gap-1.5" data-id="element-141">
                <BadgeCheck className="w-4 h-4 text-emerald-400" data-id="element-142" />
                +3.000 docentes ya accedieron
              </span>
              <span className="flex items-center gap-1.5" data-id="element-143">
                <Shield className="w-4 h-4 text-emerald-400" data-id="element-144" />
                Compra protegida
              </span>
              <span className="flex items-center gap-1.5" data-id="element-145">
                <Lock className="w-4 h-4 text-emerald-400" data-id="element-146" />
                Transacción encriptada SSL
              </span>
            </div>
          </motion.div>

          <motion.div className="bg-white text-slate-900 rounded-3xl p-8 md:p-12 shadow-2xl max-w-xl mx-auto border-4 border-emerald-500 relative overflow-hidden" initial={{
          scale: 0.95
        }} whileInView={{
          scale: 1
        }} viewport={{
          once: true
        }} data-id="element-147">
            <div className="block group" data-id="element-148">
              <div className="flex justify-center mb-8" data-id="element-149">
                <div className="relative" style={{
                width: '320px',
                height: '220px'
              }} data-id="element-150">
                  <div className="absolute inset-0 bg-emerald-400/20 blur-3xl rounded-full scale-75" data-id="element-151" />
                  <img src={IMAGES.tomo1} alt="Tomo 1" loading="lazy" decoding="async" className="absolute rounded-lg shadow-md" style={{
                  width: '72px',
                  left: '8px',
                  bottom: '10px',
                  transform: 'rotate(-14deg)',
                  zIndex: 10
                }} data-id="element-152" />
                  <img src={IMAGES.tomo2} alt="Tomo 2" loading="lazy" decoding="async" className="absolute rounded-lg shadow-md" style={{
                  width: '76px',
                  left: '52px',
                  bottom: '18px',
                  transform: 'rotate(-7deg)',
                  zIndex: 20
                }} data-id="element-153" />
                  <img src={IMAGES.tomo3} alt="Tomo 3" loading="lazy" decoding="async" className="absolute rounded-lg shadow-md" style={{
                  width: '80px',
                  left: '50%',
                  bottom: '24px',
                  transform: 'translateX(-50%) rotate(0deg)',
                  zIndex: 25
                }} data-id="element-154" />
                  <img src={IMAGES.tomo4} alt="Tomo 4" loading="lazy" decoding="async" className="absolute rounded-lg shadow-md" style={{
                  width: '76px',
                  right: '52px',
                  bottom: '18px',
                  transform: 'rotate(7deg)',
                  zIndex: 20
                }} data-id="element-155" />
                  <img src={IMAGES.tomo5} alt="Tomo 5" loading="lazy" decoding="async" className="absolute rounded-lg shadow-md" style={{
                  width: '72px',
                  right: '8px',
                  bottom: '10px',
                  transform: 'rotate(14deg)',
                  zIndex: 10
                }} data-id="element-156" />
                  <img src={IMAGES.bono1} alt="Bono 1" loading="lazy" decoding="async" className="absolute rounded opacity-80 shadow-md" style={{
                  width: '40px',
                  top: '0px',
                  left: '24px',
                  transform: 'rotate(-8deg)',
                  zIndex: 5
                }} data-id="element-157" />
                  <img src={IMAGES.bono2} alt="Bono 2" loading="lazy" decoding="async" className="absolute rounded opacity-80 shadow-md" style={{
                  width: '40px',
                  top: '0px',
                  left: '90px',
                  transform: 'rotate(-3deg)',
                  zIndex: 5
                }} data-id="element-158" />
                  <img src={IMAGES.bono3} alt="Bono 3" loading="lazy" decoding="async" className="absolute rounded opacity-80 shadow-md" style={{
                  width: '40px',
                  top: '0px',
                  right: '90px',
                  transform: 'rotate(3deg)',
                  zIndex: 5
                }} data-id="element-159" />
                  <img src={IMAGES.bono4} alt="Bono 4" loading="lazy" decoding="async" className="absolute rounded opacity-80 shadow-md" style={{
                  width: '40px',
                  top: '0px',
                  right: '24px',
                  transform: 'rotate(8deg)',
                  zIndex: 5
                }} data-id="element-160" />
                  <img src={IMAGES.product} alt="MegaPack" loading="lazy" decoding="async" className="absolute drop-shadow-xl" style={{
                  width: '110px',
                  left: '50%',
                  bottom: '4px',
                  transform: 'translateX(-50%)',
                  zIndex: 40
                }} data-id="element-161" />
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 z-50 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white text-[11px] font-bold px-4 py-1 rounded-full shadow-lg whitespace-nowrap border border-emerald-400/50" data-id="element-162">
                    5 Tomos + 4 Bonos
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center justify-center mb-8" data-id="element-163">
                {!isUSD && <>
                    <div className="text-4xl mb-1" data-id="element-164">{countryFlag}</div>
                    <div className="text-sm text-slate-500 font-medium mb-3" data-id="element-165">
                      Precio en tu moneda
                    </div>
                  </>}
                <div className="flex items-center gap-3 mb-2" data-id="element-166">
                  <span className="text-xl text-slate-400 line-through font-bold" data-id="element-167">
                    {formatPrice(47)}
                  </span>
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full uppercase" data-id="element-168">
                    Ahorras 70%
                  </span>
                </div>
                <div className="flex flex-col items-center" data-id="element-169">
                  <span className="text-5xl md:text-6xl font-extrabold text-slate-900 text-center leading-tight" data-id="element-170">
                    {formatPrice(13.99)}
                  </span>
                  {!isUSD && <span className="text-xs text-slate-500 mt-3 text-center max-w-xs" data-id="element-171">
                      Precio aproximado en {currencyCode} · El cobro final se
                      realiza en USD
                    </span>}
                  {isUSD && <span className="text-sm font-bold text-emerald-600 uppercase tracking-wide mt-2" data-id="element-172">
                      Pago único
                    </span>}
                </div>
              </div>

              {/* Compra protegida badge */}
              <div className="flex items-center justify-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-2 mb-6 mx-auto w-fit" data-id="element-173">
                <ShieldCheck className="w-4 h-4 text-emerald-600" data-id="element-174" />
                <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide" data-id="element-175">
                  Compra 100% protegida
                </span>
              </div>

              <div className="space-y-3 mb-8 text-left max-w-xs mx-auto" data-id="element-176">
                {['Pago único — sin cargos ocultos', 'Acceso de por vida', 'Descarga inmediata', 'Actualizaciones futuras incluidas', 'Soporte por email incluido'].map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-177">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" data-id="element-178" />
                    <span className="text-slate-700 font-medium" data-id="element-179">{item}</span>
                  </div>)}
              </div>

              <CTAButton text="Sí, quiero el sistema completo con descuento" fullWidth size="xl" className="mb-4" scrollToSection={false} checkoutUrl={checkoutUrl} data-id="element-180" />

              {/* Enhanced trust block */}
              <div className="space-y-3 mb-6" data-id="element-181">
                <div className="flex items-center justify-center gap-2 text-xs text-slate-400" data-id="element-182">
                  <Lock className="w-3 h-3" data-id="element-183" />
                  <span data-id="element-184">Pago 100% seguro · Transacción encriptada con SSL</span>
                </div>
                <div className="flex items-center justify-center gap-2 text-xs text-slate-400" data-id="element-185">
                  <ShieldCheck className="w-3 h-3" data-id="element-186" />
                  <span data-id="element-187">Garantía de devolución total por 30 días</span>
                </div>
              </div>

              <div className="flex items-center justify-center gap-6 opacity-50 grayscale" data-id="element-188">
                <img src="https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg" alt="PayPal" loading="lazy" decoding="async" className="h-6" width="80" height="24" data-id="element-189" />
                <img src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" alt="Visa" loading="lazy" decoding="async" className="h-6" width="60" height="24" data-id="element-190" />
                <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" loading="lazy" decoding="async" className="h-6" width="40" height="24" data-id="element-191" />
              </div>
              <p className="text-[10px] text-slate-400 mt-3 text-center" data-id="element-192">
                Procesado de forma segura por Hotmart®
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 10: TESTIMONIALS */}
      <section className="py-20 px-4 bg-slate-950 border-b border-slate-800" data-id="element-193">
        <div className="max-w-6xl mx-auto" data-id="element-194">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4 max-w-3xl mx-auto" data-id="element-195">
            Lo que dicen los docentes que ya usan el MegaPack:
          </h2>
          <p className="text-center text-sm text-slate-400 mb-16 flex items-center justify-center gap-1.5" data-id="element-196">
            <BadgeCheck className="w-4 h-4 text-emerald-400" data-id="element-197" />
            Opiniones verificadas de compradores reales
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-198">
            {[{
            text: 'Antes tardaba horas preparando cada clase. Con este material ya tengo todo estructurado.',
            name: 'María González',
            age: 34,
            location: 'Buenos Aires, Argentina'
          }, {
            text: 'Las actividades funcionan muy bien con los alumnos.',
            name: 'Carlos Ramírez',
            age: 41,
            location: 'CDMX, México'
          }, {
            text: 'Me ahorró muchísimo tiempo durante todo el año.',
            name: 'Ana López',
            age: 29,
            location: 'Bogotá, Colombia'
          }, {
            text: 'Las evaluaciones listas me solucionaron un problema enorme.',
            name: 'Sofía Navarro',
            age: 37,
            location: 'Montevideo, Uruguay'
          }, {
            text: 'Las clases salvavidas me ayudaron varias veces.',
            name: 'Juan Martínez',
            age: 45,
            location: 'Lima, Perú'
          }, {
            text: 'Todo está organizado y listo para usar.',
            name: 'Laura Vega',
            age: 32,
            location: 'Quito, Ecuador'
          }].map((t, i) => <div key={i} className="bg-slate-900 border border-slate-700 p-6 rounded-2xl shadow-lg" data-id="element-199">
                <div className="flex items-center justify-between mb-4" data-id="element-200">
                  <div className="flex gap-1 text-yellow-400" data-id="element-201">
                    {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-5 h-5 fill-current" data-id="element-202" />)}
                  </div>
                  <span className="flex items-center gap-1 text-[10px] text-emerald-400 font-medium" data-id="element-203">
                    <BadgeCheck className="w-3 h-3" data-id="element-204" /> Verificado
                  </span>
                </div>
                <p className="text-slate-300 italic text-lg leading-relaxed mb-4" data-id="element-205">
                  "{t.text}"
                </p>
                <div className="text-sm" data-id="element-206">
                  <p className="text-white font-bold" data-id="element-207">
                    {t.name}, {t.age} años
                  </p>
                  <p className="text-slate-400" data-id="element-208">{t.location}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 11: GUARANTEE */}
      <section className="py-20 px-4 bg-slate-900 border-b border-slate-800 text-center" data-id="element-209">
        <div className="max-w-2xl mx-auto" data-id="element-210">
          <div className="w-24 h-24 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-8 border border-emerald-500/30" data-id="element-211">
            <ShieldCheck className="w-12 h-12 text-emerald-400" data-id="element-212" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-6" data-id="element-213">
            Garantía MEGAPACK LISTO PARA IMPRIMIR — 30 días
          </h2>
          <p className="text-xl text-slate-300 mb-4" data-id="element-214">
            Abrís los tomos. Revisás las clases. Podés probar el material en el
            aula.
          </p>
          <p className="text-xl text-slate-300 mb-6" data-id="element-215">
            Si en 30 días sentís que no es lo que esperabas:
          </p>
          <p className="text-2xl font-bold text-emerald-400 mb-6" data-id="element-216">
            Te devolvemos el 100% del dinero.
          </p>
          <p className="text-slate-400" data-id="element-217">
            Sin preguntas. Sin formularios. Sin complicaciones.
          </p>
        </div>
      </section>

      {/* SECTION 12: FAQ */}
      <section className="py-20 px-4 bg-slate-950 border-b border-slate-800" data-id="element-218">
        <div className="max-w-3xl mx-auto" data-id="element-219">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12" data-id="element-220">
            Preguntas frecuentes
          </h2>
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 md:p-8 shadow-xl mb-16" data-id="element-221">
            <AccordionItem question="¿Cómo recibo el MegaPack después de comprar?" answer="Recibirás acceso inmediato para descargar todos los tomos y bonos en formato PDF." data-id="element-222" />
            <AccordionItem question="¿Sirve para mi país?" answer="Sí. El contenido está diseñado para docentes de informática y puede adaptarse fácilmente a cualquier currículo de Latinoamérica." data-id="element-223" />
            <AccordionItem question="¿Puedo editar el material?" answer="Los archivos están en PDF listos para usar y podés adaptarlos según tu clase." data-id="element-224" />
            <AccordionItem question="¿Incluye los bonos?" answer="Sí. Todos los bonos están incluidos con la compra." data-id="element-225" />
            <AccordionItem question="¿Funciona si mi escuela tiene pocos recursos tecnológicos?" answer="Sí. Muchas actividades están pensadas para trabajar incluso con pocos recursos." data-id="element-226" />
            <AccordionItem question="¿Está actualizado?" answer="Sí. El contenido está diseñado para trabajar informática actual aplicada al aula." data-id="element-227" />
            <AccordionItem question="¿Tiene garantía?" answer="Sí. Tenés 30 días para probar todo el material y pedir un reembolso completo si no es lo que esperabas." data-id="element-228" />
          </div>
          <div className="text-center" data-id="element-229">
            <CTAButton text="QUIERO MI MEGAPACK AHORA 🚀" size="xl" scrollToSection={true} data-id="element-230" />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-950 text-slate-500 py-12 px-4 text-center text-sm" data-id="element-231">
        <div className="max-w-6xl mx-auto" data-id="element-232">
          <p className="mb-4" data-id="element-233">
            © 2025 MegaPack Informática Lista™. Todos los derechos reservados.
          </p>
          <p className="mb-8 max-w-2xl mx-auto" data-id="element-234">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            Además, este sitio no está respaldado por Facebook de ninguna
            manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </p>
          <div className="flex justify-center gap-4" data-id="element-235">
            <a href="#" className="hover:text-slate-300 transition-colors" data-id="element-236">
              Términos y Condiciones
            </a>
            <a href="#" className="hover:text-slate-300 transition-colors" data-id="element-237">
              Política de Privacidad
            </a>
            <a href="#" className="hover:text-slate-300 transition-colors" data-id="element-238">
              Contacto
            </a>
          </div>
        </div>
      </footer>

      {/* STICKY MOBILE CTA */}
      <AnimatePresence data-id="element-239">
        {showStickyCTA && <motion.div initial={{
        y: 100
      }} animate={{
        y: 0
      }} exit={{
        y: 100
      }} className="fixed bottom-0 left-0 w-full bg-slate-900 border-t border-slate-800 p-4 shadow-[0_-4px_15px_rgba(0,0,0,0.5)] z-40 md:hidden flex items-center justify-between gap-4" data-id="element-240">
            <div className="flex flex-col" data-id="element-241">
              <span className="text-xs text-slate-500 line-through" data-id="element-242">
                {formatPrice(47)}
              </span>
              <span className="font-bold text-2xl text-white" data-id="element-243">
                {formatPrice(13.99)}
              </span>
            </div>
            <CTAButton text="COMPRAR AHORA" size="md" fullWidth={false} scrollToSection={false} showIcon={false} checkoutUrl={checkoutUrl} data-id="element-244" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}